/**
 * German language keywords for category prediction
 */
import type {
  AccommodationSubcategoryKeywords,
  ContextKeywords,
  DrinksConfig,
  DrinksSubcategoryKeywords,
  EntertainmentSubcategoryKeywords,
  FoodConfig,
  FoodSubcategoryKeywords,
  HealthcareSubcategoryKeywords,
  LanguageKeywords,
  MLPatternKeywords,
  ShoppingContextKeywords,
  ShoppingSubcategoryKeywords,
  StemmingConfig,
  TransportConfig,
  TransportKeywords,
  TransportSubcategoryKeywords,
  UtilitiesSubcategoryKeywords,
} from '../types';

// Transport-related keywords for disambiguation
export const transportKeywords: TransportKeywords = {
  general: [
    'zug',
    'bahn',
    'bus',
    'flug',
    'metro',
    'u-bahn',
    'straßenbahn',
    'straßenbahn',
    'flugzeug',
    'taxi',
    'uber',
    'fahrt',
    'transport',
    'verkehr',
    'reise',
    'fahrkarte',
    'ticket',
    'airline',
    'eisenbahn',
    'fernbus',
    'shuttle',
    'fähre',
    'boot',
    'auto',
    'pkw',
    'reise',
    'fahrt',
    'pendeln',
    'bahnhof',
    'flughafen',
    'haltestelle',
  ],
  tickets: ['ticket', 'fahrkarte', 'pass', 'fahrtkosten', 'fahrpreis'],
  tripContext: [
    'reise',
    'ausflug',
    'urlaub',
    'fahrt',
    'trip',
    'roadtrip',
    'wochenendausflug',
    'geschäftsreise',
    'dienstreise',
  ],
};

// Transport subcategory keywords
export const transportSubcategories: TransportSubcategoryKeywords = {
  car: [
    'autofinanzierung',
    'autokredit',
    'autoversicherung',
    'kfz-versicherung',
    'kfz-steuer',
    'kfz-haftpflicht',
    'autowäsche',
    'autowaschanlage',
    'tüv',
    'hauptuntersuchung',
    'autoaufbereitung',
  ],
  // DE common words only — brand names live in `../shared/brands.ts`.
  bus: ['bus', 'omnibus', 'fernbus', 'reisebus', 'linienbus', 'stadtbus'],
  train: [
    'zug',
    'bahn',
    'eisenbahn',
    'regionalzug',
    'regionalbahn',
    'nahverkehr',
    'fernverkehr',
  ],
  tram: ['straßenbahn', 'tram', 'stadtbahn'],
  flight: ['flug', 'flugzeug', 'airline', 'boardingpass', 'boarding'],
  bike: [
    'fahrrad',
    'rad',
    'bike',
    'e-bike',
    'pedelec',
    'mountainbike',
    'rennrad',
    'fahrradverleih',
  ],
  boat: ['boot', 'schiff', 'fähre', 'kreuzfahrt', 'dampfer', 'yacht'],
  parking: [
    'parken',
    'parkhaus',
    'parkplatz',
    'tiefgarage',
    'parkschein',
    'parkgebühr',
    'parkuhr',
  ],
  taxi: ['taxi', 'mietwagen mit fahrer', 'taxistand'],
  rideshare: [
    'mitfahrgelegenheit',
    'fahrgemeinschaft',
    'ridesharing',
    'mitfahren',
  ],
  metro: ['u-bahn', 'metro', 'untergrundbahn'],
  fuel: ['benzin', 'diesel', 'sprit', 'kraftstoff', 'tanken', 'tankstelle'],
  toll: ['maut', 'autobahngebühr', 'straßengebühr', 'vignette'],
  rental: ['mietwagen', 'autovermietung'],
  maintenance: [
    'inspektion',
    'werkstatt',
    'autowerkstatt',
    'ölwechsel',
    'reifenwechsel',
    'bremsen',
    'kfz-mechaniker',
    'autoservice',
  ],
  airport: [
    'flughafen',
    'airport',
    'terminal flughafen',
    'flughafenparken',
    'flughafentransfer',
    'flughafenshuttle',
    'lounge flughafen',
  ],
};

// Only the generic word is shared by food and healthcare: on its own it
// cannot say sports nutrition or health, so the cloud tier breaks that
// tie. Specific products go to one parent so the free tier can answer.
const supplementsKeywords: string[] = [
  'nahrungsergänzung',
  'nahrungsergaenzung',
  'nahrungsergänzungsmittel',
  'supplements',
];
const sportsNutritionKeywords: string[] = [
  'protein',
  'proteinpulver',
  'eiweißpulver',
  'eiweisspulver',
  'eiweiss',
  'whey',
  'kreatin',
  'bcaa',
  'pre workout',
];
const healthSupplementsKeywords: string[] = [
  'kollagen',
  'collagen',
  'vitamine',
  'vitamin',
  'omega 3',
  'omega-3',
  'omega3',
  'probiotika',
  'magnesium',
  'multivitamin',
  'zink',
];

// Food subcategory keywords
export const foodSubcategories: FoodSubcategoryKeywords = {
  // DE common words only — brands + universal food terms live in
  // `../shared/brands.ts`.
  pizza: ["joey's"],
  sushi: ['japaner', 'wasabi'],
  hamburger: [],
  sandwich: ['baguette', 'döner'],
  soup: ['suppe', 'eintopf', 'brühe', 'bouillon'],
  steak: ['rumpsteak', 'filet'],
  salad: [
    'salat',
    'caesar salat',
    'gemischter salat',
    'beilagensalat',
    'salatbar',
  ],
  pastry: [
    'gebäck',
    'brötchen',
    'plunder',
    'hefeteilchen',
    'kuchen',
    'berliner',
  ],
  dessert: ['nachtisch', 'nachspeise', 'süßspeise'],
  candy: [
    'süßigkeiten',
    'bonbon',
    'schokolade',
    'schokoriegel',
    'gummibärchen',
  ],
  icecream: ['eis', 'eiscreme', 'eisdiele'],
  snacks: ['knabbersachen', 'bonbons'],
  fruit: ['obst', 'früchte', 'apfel'],
  vegetables: ['gemüse', 'salat', 'karotten', 'tomaten', 'gurken', 'paprika'],
  meat: ['fleisch', 'wurst', 'schinken', 'rindfleisch', 'schweinefleisch'],
  seafood: ['meeresfrüchte', 'fisch', 'lachs', 'thunfisch', 'garnelen'],
  dairy: ['milchprodukte', 'milch', 'käse', 'joghurt', 'quark'],
  breakfast: [
    'frühstück',
    'morgenessen',
    'müsli',
    'marmelade',
    'frühstücksbuffet',
  ],
  restaurant: [
    'restaurant',
    'gaststätte',
    'wirtshaus',
    'gasthaus',
    'lokal',
    'brasserie',
    'ristorante',
    'trattoria',
    'bistro',
    'fastfood',
    'schnellimbiss',
    'imbiss',
    'snackbar',
    'nordsee',
    'mittagessen',
    'lunch',
    'mittag',
    'abendessen',
    'dinner',
    'abendbrot',
    'catering',
    'partyservice',
    'buffet',
    'festessen',
  ],
  bakery: ['bäckerei', 'backshop', 'bäcker', 'konditorei', 'patisserie'],
  cafe: ['café', 'coffeeshop', 'kaffeehaus'],
  delivery: ['lieferung'],
  groceries: ['supermarkt', 'lebensmittel', 'einkauf'],
  supplements: [...sportsNutritionKeywords, ...supplementsKeywords],
};

export const drinksSubcategories: DrinksSubcategoryKeywords = {
  // DE common words only — brands live in `../shared/brands.ts`.
  // Bare "water" words stay out: they collide with utilities/water.
  drinks: ['getränke', 'getränk', 'erfrischung'],
  water: [
    'mineralwasser',
    'wasserflasche',
    'flasche wasser',
    'sprudelwasser',
    'stilles wasser',
  ],
  juice: [
    'saft',
    'säfte',
    'orangensaft',
    'apfelsaft',
    'apfelschorle',
    'saftschorle',
  ],
  soda: [
    'softdrink',
    'softdrinks',
    'limonade',
    'limo',
    'cola',
    'spezi',
    'fassbrause',
    'club-mate',
    'club mate',
  ],
  coffee: [
    'kaffee',
    'milchkaffee',
    'eiskaffee',
    'filterkaffee',
    'kaffee to go',
  ],
  tea: ['tee', 'grüner tee', 'kräutertee', 'eistee'],
  energy: [
    'energydrink',
    'energydrinks',
    'energy drink',
    'sportgetränk',
    'isotonisches getränk',
  ],
  // Bare 'weizen' would match 'Weizenmehl' through the substring rule.
  beer: [
    'bier',
    'biere',
    'bierchen',
    'feierabendbier',
    'bierkasten',
    'kasten bier',
    'pils',
    'weizenbier',
    'hefeweizen',
    'weißbier',
    'weissbier',
    'helles',
    'kölsch',
    'radler',
  ],
  wine: [
    'wein',
    'weine',
    'rotwein',
    'weißwein',
    'weinschorle',
    'sekt',
    'champagner',
    'glühwein',
    'weinhandlung',
  ],
  cocktails: ['longdrink', 'longdrinks'],
  spirits: ['spirituosen', 'schnaps', 'schnäpse', 'wodka', 'likör', 'obstler'],
  alcohol: ['alkohol', 'alkoholische getränke', 'absacker', 'umtrunk'],
  bar: [
    'bar',
    'kneipe',
    'biergarten',
    'cocktailbar',
    'weinbar',
    'sportsbar',
    'lounge',
    'pub',
  ],
};

// Entertainment subcategory keywords
export const entertainmentSubcategories: EntertainmentSubcategoryKeywords = {
  // DE common words only — brands + universal terms live in
  // `../shared/brands.ts`.
  movies: ['kino', 'film', 'movie', 'premiere'],
  events: [
    'konzert',
    'musik',
    'band',
    'festival',
    'konzerthalle',
    'philharmonie',
    'oper',
    'operette',
    'museum',
    'galerie',
    'kunstmuseum',
    'ausstellung',
    'kulturzentrum',
    'gedenkstätte',
    'veranstaltung',
    'event',
    'messe',
    'konferenz',
    'workshop',
    'seminar',
    'party',
    'fest',
    'volksfest',
    'musikfestival',
    'theater',
    'schauspiel',
    'bühne',
    'staatstheater',
    'stadttheater',
    'nachtleben',
    'club',
    'diskothek',
    'disco',
    'tanzbar',
    'nachtclub',
  ],
  sports: [
    'sport',
    'fußball',
    'stadion',
    'arena',
    'sportevent',
    'match',
    'turnier',
    'tennis',
    'basketball',
  ],
  gaming: ['spiel', 'videospiel', 'konsole'],
  streaming: [],
  books: ['buch', 'bücher', 'buchhandlung'],
  outdoor: [
    'park',
    'freizeitpark',
    'vergnügungspark',
    'zoo',
    'tierpark',
    'botanischer garten',
  ],
  music: ['musik', 'konzert', 'livemusik', 'oper', 'philharmonie'],
  fitness: ['fitnessstudio', 'fitness', 'training', 'gym'],
  photography: [
    'fotografie',
    'kamera',
    'fotoshooting',
    'fotograf',
    'foto',
    'bilder',
  ],
  podcasts: ['talk-show'],
  water: [
    'wassersport',
    'schwimmen',
    'surfen',
    'tauchen',
    'kanu',
    'kajak',
    'segeln',
  ],
};

// Shopping subcategory keywords
export const shoppingSubcategories: ShoppingSubcategoryKeywords = {
  // DE common words only — brands live in `../shared/brands.ts`.
  clothes: [
    'kleidung',
    'klamotten',
    'mode',
    'hemd',
    'hose',
    'kleid',
    'jacke',
    'mantel',
  ],
  electronics: [
    'elektronik',
    'technik',
    'haushaltsgeräte',
    'küche',
    'waschmaschine',
    'kühlschrank',
    'geschirrspüler',
    'kaffeemaschine',
    'kaffeevollautomat',
  ],
  furniture: ['haushalt', 'möbel', 'deko', 'einrichtung'],
  cosmetics: ['kosmetik', 'parfümerie', 'parfum', 'makeup'],
  toys: ['spielzeug', 'spielwaren'],
  sports: ['sport', 'sportartikel'],
  books: ['bücher', 'buchhandlung', 'bücherei'],
  gifts: ['geschenke', 'geschenkideen', 'present', 'mitbringsel', 'andenken'],
  jewelry: [
    'accessoires',
    'schmuck',
    'uhren',
    'taschen',
    'gürtel',
    'sonnenbrille',
  ],
  shoes: ['schuhe', 'sneaker', 'stiefel', 'sandalen', 'pumps', 'schuhgeschäft'],
  groceries: ['lebensmittel', 'supermarkt'],
  online: ['online shop', 'onlineshop', 'versand', 'bestellung'],
  services: [
    'reinigung',
    'wäscherei',
    'reparatur',
    'schneider',
    'änderung',
    'dienstleistung',
    'service',
  ],
  mall: [
    'einkaufszentrum',
    'shoppingcenter',
    'shopping center',
    'galerie',
    'passage',
    'arkaden',
  ],
  market: [
    'markt',
    'wochenmarkt',
    'bauernmarkt',
    'flohmarkt',
    'weihnachtsmarkt',
    'naschmarkt',
  ],
  department: ['kaufhaus', 'warenhaus'],
  hardware: ['baumarkt', 'werkzeug', 'eisenwaren'],
  garden: [
    'gartencenter',
    'garten',
    'pflanzen',
    'blumen',
    'samen',
    'baumschule',
    'gärtnerei',
  ],
};

// Accommodation subcategory keywords
export const accommodationSubcategories: AccommodationSubcategoryKeywords = {
  // DE common words only — brands live in `../shared/brands.ts`.
  hotel: [
    'hotel',
    'pension',
    'herberge',
    'resort',
    'ferienanlage',
    'all inclusive',
    'motel',
    'rasthaus',
    'zimmer',
    'einzelzimmer',
    'doppelzimmer',
    'suite',
  ],
  airbnb: ['kurzzeitvermietung', 'privatvermietung'],
  hostel: ['hostel', 'jugendherberge', 'backpacker'],
  apartment: ['apartment', 'wohnung', 'ferienwohnung', 'studio'],
  house: ['haus', 'einfamilienhaus', 'villa', 'bungalow', 'ferienhaus'],
  rent: [
    'miete',
    'mietzahlung',
    'kaltmiete',
    'warmmiete',
    'nebenkosten',
    'kaution',
  ],
  camping: [
    'camping',
    'campingplatz',
    'zelt',
    'wohnwagen',
    'wohnmobil',
    'caravan',
    'stellplatz',
  ],
};

// Healthcare subcategory keywords
export const healthcareSubcategories: HealthcareSubcategoryKeywords = {
  // DE common words only — brand chains live in `../shared/brands.ts`.
  doctor: [
    'arzt',
    'doktor',
    'hausarzt',
    'facharzt',
    'praxis',
    'sprechstunde',
    'behandlung',
  ],
  pharmacy: [
    'apotheke',
    'medikamente',
    'rezept',
    'arznei',
    'tabletten',
    'notdienst apotheke',
    'online apotheke',
  ],
  hospital: [
    'krankenhaus',
    'klinik',
    'kliniken',
    'universitätsklinikum',
    'notaufnahme',
    'notfall',
    'rettung',
    'notarzt',
    'labor',
    'bluttest',
    'untersuchung',
    'analyse',
  ],
  dental: ['zahnarzt', 'zahnarztpraxis', 'zähne', 'dental', 'zahnbehandlung'],
  vision: ['augenarzt', 'optiker', 'brille', 'kontaktlinsen', 'sehtest'],
  specialist: [
    'psychologe',
    'psychotherapeut',
    'psychiater',
    'therapie',
    'beratung',
    'facharzt',
    'spezialist',
    'fachpraxis',
    'physiotherapie',
    'massage',
    'krankengymnastik',
    'reha',
  ],
  insurance: ['versicherung', 'krankenkasse', 'gesetzlich'],
  fitness: ['wellness', 'spa', 'entspannung', 'massage', 'sauna'],
  supplements: [...healthSupplementsKeywords, ...supplementsKeywords],
};

// Utilities subcategory keywords
export const utilitiesSubcategories: UtilitiesSubcategoryKeywords = {
  // DE common words only — brand providers live in `../shared/brands.ts`.
  electricity: ['strom', 'stromrechnung', 'energie'],
  water: ['wasser', 'wasserwerke', 'abwasser', 'wasserzähler'],
  gas: [
    'gas',
    'gasrechnung',
    'heizung',
    'erdgas',
    'fernwärme',
    'heizkosten',
    'brennstoff',
  ],
  internet: ['internet', 'wlan'],
  phone: ['telefon', 'mobilfunk', 'handy', 'smartphone', 'festnetz'],
  trash: ['müll', 'abfall', 'entsorgung', 'müllabfuhr', 'wertstoff'],
  maintenance: [
    'wartung',
    'reparatur',
    'instandhaltung',
    'hausmeister',
    'handwerker',
    'klempner',
    'elektriker',
    'sanitär',
  ],
  streaming: [],
};

// Context keywords for better detection
export const context: ContextKeywords = {
  flight: [
    'flug',
    'flughafen',
    'boarding',
    'gate',
    'terminal',
    'check-in',
    'gepäck',
    'airline',
    'pilot',
    'crew',
  ],
  fuel: [
    'benzin',
    'diesel',
    'tankstelle',
    'tanken',
    'sprit',
    'kraftstoff',
    'tank voll',
    'vollgetankt',
  ],
  transportTickets: [
    'fahrkarte',
    'ticket',
    'pass',
    'monatskarte',
    'jahreskarte',
    'einzelfahrt',
    'hin und zurück',
  ],
};

// Shopping context keywords
export const shoppingContext: ShoppingContextKeywords = {
  clothes: ['mode', 'kleidung', 'anziehen', 'outfit', 'stil'],
  electronics: ['technik', 'gadget', 'device', 'elektronik', 'digital'],
  furniture: ['möbel', 'einrichtung', 'wohnung', 'haus', 'deko'],
  online: ['online', 'amazon', 'ebay', 'versand'],
  groceries: ['lebensmittel', 'essen', 'küche', 'kochen', 'einkauf'],
  services: ['service', 'dienstleistung', 'reparatur', 'wartung', 'pflege'],
};

// Food configuration
export const foodConfig: FoodConfig = {
  obviousSubcategories: [
    'pizza',
    'sushi',
    'hamburger',
    'sandwich',
    'soup',
    'breakfast',
    'lunch',
    'dinner',
    'dessert',
  ],
  subcategoryPriority: [
    'pizza',
    'sushi',
    'hamburger',
    'sandwich',
    'soup',
    'cafe',
    'bakery',
    'fastfood',
    'restaurant',
    'delivery',
    'groceries',
    'snacks',
    'breakfast',
    'lunch',
    'dinner',
    'dessert',
    'fruit',
    'vegetables',
    'meat',
    'seafood',
    'dairy',
    'catering',
  ],
};

const drinksConfig: DrinksConfig = {
  subcategoryPriority: [
    'cocktails',
    'beer',
    'wine',
    'spirits',
    'energy',
    'coffee',
    'tea',
    'juice',
    'soda',
    'water',
    'bar',
    'alcohol',
    'drinks',
  ],
};

// Transport configuration
export const transportConfig: TransportConfig = {
  subcategoryPriority: [
    'flight',
    'train',
    'car',
    'bus',
    'tram',
    'metro',
    'taxi',
    'bike',
    'boat',
    'rideshare',
    'parking',
    'fuel',
    'toll',
    'rental',
    'maintenance',
    'airport',
  ],
};

// ML pattern keywords for fast category matching
export const mlPatterns: MLPatternKeywords = {
  expense: {
    transport: [
      'fahrt',
      'reise',
      'ticket',
      'taxi',
      'zug',
      'flug',
      'bus',
      'auto',
      'tanken',
      'parken',
    ],
    food: [
      'essen',
      'restaurant',
      'pizza',
      'café',
      'supermarkt',
      'lebensmittel',
      'trinken',
      'bar',
      'mittagessen',
      'frühstück',
    ],
    entertainment: [
      'kino',
      'konzert',
      'sport',
      'spiel',
      'party',
      'festival',
      'theater',
      'club',
      'museum',
      'spaß',
    ],
    shopping: [
      'kaufen',
      'shopping',
      'geschäft',
      'laden',
      'kleidung',
      'elektronik',
      'möbel',
      'amazon',
      'bestellen',
      'online',
    ],
    healthcare: [
      'arzt',
      'apotheke',
      'medikament',
      'krankenhaus',
      'gesundheit',
      'zahnarzt',
      'therapie',
      'versicherung',
      'behandlung',
      'medizin',
    ],
    utilities: [
      'strom',
      'gas',
      'wasser',
      'internet',
      'telefon',
      'miete',
      'nebenkosten',
      'versicherung',
      'rechnung',
      'stadtwerke',
    ],
    accommodation: [
      'hotel',
      'übernachtung',
      'airbnb',
      'hostel',
      'zimmer',
      'unterkunft',
      'pension',
      'ferienwohnung',
      'resort',
      'booking',
    ],
    education: [
      'schule',
      'universität',
      'kurs',
      'fortbildung',
      'seminar',
      'buch',
      'lernen',
      'ausbildung',
      'studium',
      'bildung',
    ],
  },
  group: {
    friends: [
      'freunde',
      'kumpel',
      'party',
      'ausgehen',
      'treffen',
      'spaß',
      'wochenende',
      'freizeit',
      'gesellschaft',
      'clique',
    ],
    family: [
      'familie',
      'eltern',
      'geschwister',
      'verwandtschaft',
      'familienfest',
      'geburtstag',
      'weihnachten',
      'ostern',
      'familienurlaub',
      'verwandte',
    ],
    couple: [
      'paar',
      'partner',
      'freundin',
      'freund',
      'beziehung',
      'date',
      'romantisch',
      'zusammen',
      'liebe',
      'gemeinsam',
    ],
    events: [
      'veranstaltung',
      'event',
      'feier',
      'party',
      'hochzeit',
      'geburtstag',
      'jubiläum',
      'fest',
      'celebration',
      'feiern',
    ],
    roommates: [
      'mitbewohner',
      'wg',
      'wohngemeinschaft',
      'zusammenwohnen',
      'haushalt',
      'mitbewohnerin',
      'flatmate',
      'zimmergenossen',
      'wohnung teilen',
      'gemeinsam wohnen',
    ],
    trip: [
      'reise',
      'urlaub',
      'ausflug',
      'trip',
      'wochenendausflug',
      'städtetrip',
      'roadtrip',
      'backpacking',
      'fernreise',
      'kurzurlaub',
    ],
    work: [
      'arbeit',
      'kollegen',
      'büro',
      'firma',
      'team',
      'projekt',
      'geschäftlich',
      'beruflich',
      'meeting',
      'konferenz',
    ],
  },
};

// German stemming configuration
export const stemming: StemmingConfig = {
  suffixes: [
    'en',
    'er',
    'es',
    'e',
    'n',
    'te',
    'ten',
    'ung',
    'heit',
    'keit',
    'lich',
    'ig',
    'isch',
    'bar',
    'los',
    'voll',
    'weg',
    'hin',
    'her',
    'ab',
    'an',
    'auf',
    'aus',
    'bei',
    'durch',
    'für',
    'gegen',
    'hinter',
    'in',
    'mit',
    'nach',
    'neben',
    'ohne',
    'über',
    'um',
    'unter',
    'von',
    'vor',
    'zu',
    'zwischen',
  ],
};

// Complete German language keywords export
export const deKeywords: LanguageKeywords = {
  transport: transportKeywords,
  transportSubcategories,
  foodSubcategories: foodSubcategories as FoodSubcategoryKeywords,
  drinksSubcategories,
  entertainmentSubcategories,
  shoppingSubcategories,
  accommodationSubcategories,
  healthcareSubcategories,
  utilitiesSubcategories,
  context,
  shoppingContext,
  foodConfig,
  drinksConfig,
  transportConfig,
  mlPatterns,
  stemming,
};
